import React, { useState, useEffect, useRef } from 'react';
import { View, TouchableOpacity, StyleSheet, Animated, Platform } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../context/ThemeContext';

/**
 * CollapsibleSidebar - Animated sidebar wrapper with cascade effect
 * 
 * @param {React.ReactNode} children - Menu items to render
 * @param {number} itemCount - Number of menu items (for stagger animation)
 * @param {boolean} defaultCollapsed - Initial collapsed state
 */
const CollapsibleSidebar = ({ children, itemCount = 0, defaultCollapsed = false }) => {
  const { palette } = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  
  // Animation values
  const sidebarWidth = useRef(new Animated.Value(defaultCollapsed ? 60 : 400)).current;
  const itemAnimations = useRef(
    Array(itemCount).fill(0).map(() => ({
      opacity: new Animated.Value(defaultCollapsed ? 0 : 1),
      translateX: new Animated.Value(defaultCollapsed ? -40 : 0), // Start further back
      scale: new Animated.Value(defaultCollapsed ? 0.8 : 1), // Start smaller
    }))
  ).current;

  const toggleSidebar = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);

    // Animate sidebar width with less bounce
    Animated.spring(sidebarWidth, {
      toValue: newCollapsed ? 60 : 400,
      useNativeDriver: false,
      damping: 18,              // Increased damping = less bounce
      stiffness: 140,            // Higher stiffness = faster, tighter
      overshootClamping: false,
    }).start();

    // Animate items with cascade effect
    if (newCollapsed) {
      // Collapsing: fade out quickly in reverse order (no delay)
      Animated.stagger(
        30,
        itemAnimations.slice().reverse().map((anim) =>
          Animated.parallel([
            Animated.timing(anim.opacity, {
              toValue: 0,
              duration: 150,
              useNativeDriver: true,
            }),
            Animated.timing(anim.translateX, {
              toValue: -40,
              duration: 150,
              useNativeDriver: true,
            }),
            Animated.timing(anim.scale, {
              toValue: 0.8,
              duration: 150,
              useNativeDriver: true,
            }),
          ])
        )
      ).start();
    } else {
      // Expanding: DELAY 500ms, then cascade in with bouncy flourish
      Animated.sequence([
        Animated.delay(500),      // Wait 0.5s after toggle button pressed
        Animated.stagger(
          80,
          itemAnimations.map((anim) =>
            Animated.parallel([
              Animated.spring(anim.opacity, {
                toValue: 1,
                useNativeDriver: true,
                damping: 12,
                stiffness: 120,
                overshootClamping: false,
              }),
              Animated.spring(anim.translateX, {
                toValue: 0,
                useNativeDriver: true,
                damping: 10,        // Lower damping = more bounce!
                stiffness: 100,      // Lower stiffness = slower, more pronounced
                overshootClamping: false,
              }),
              Animated.spring(anim.scale, {
                toValue: 1,
                useNativeDriver: true,
                damping: 9,         // Even more bounce on scale!
                stiffness: 90,
                overshootClamping: false,
              }),
            ])
          )
        ),
      ]).start();
    }
  };

  return (
    <Animated.View
      style={[
        styles.sidebarContainer,
        {
          width: sidebarWidth,
          borderRightColor: palette.border || 'rgba(255,255,255,0.12)',
          // Conditional styling based on collapsed state
          backgroundColor: isCollapsed ? 'transparent' : '#FF4747',
          borderRadius: isCollapsed ? 0 : 20,
          ...(isCollapsed ? {} : Platform.select({
            web: {
              boxShadow: '5px 7px 0px rgba(0,0,0,1)',
            },
            default: {
              shadowColor: '#000000',
              shadowOffset: { width: 5, height: 7 },
              shadowOpacity: 1,
              shadowRadius: 0,
              elevation: 8,
            },
          })),
        },
      ]}
    >
      {/* Toggle Button */}
      <TouchableOpacity
        style={[
          styles.toggleButton,
          {
            backgroundColor: '#FFFFFF',
            borderColor: '#000000',
          },
        ]}
        onPress={toggleSidebar}
      >
        <MaterialCommunityIcons
          name={isCollapsed ? 'chevron-right' : 'chevron-left'}
          size={24}
          color={palette.text}
        />
      </TouchableOpacity>

      {/* Content */}
      <View style={styles.content}>
        {/* Wrap the children container and animate its direct children */}
        {React.Children.map(children, (child) => {
          // If child is a View container with menu items
          if (child?.props?.children && Array.isArray(child.props.children)) {
            return React.cloneElement(child, {
              children: child.props.children.map((item, index) => {
                if (index >= itemAnimations.length || !item) return item;

                return (
                  <Animated.View
                    key={item.key || index}
                    style={{
                      opacity: itemAnimations[index].opacity,
                      transform: [
                        { translateX: itemAnimations[index].translateX },
                        { scale: itemAnimations[index].scale },
                      ],
                    }}
                  >
                    {item}
                  </Animated.View>
                );
              }),
            });
          }
          
          // Fallback: animate single child
          return child;
        })}
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  sidebarContainer: {
    maxWidth: 300,
    position: 'relative',
    overflow: 'hidden',
    // Base styling - colors and shadows now applied conditionally in component
  },
  toggleButton: {
    position: 'absolute',
    top: 20,
    right: 10,
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '2px 2px 0px rgba(0, 0, 0, 0.85)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 2, height: 2 },
        shadowOpacity: 0.85,
        shadowRadius: 0,
        elevation: 3,
      },
    }),
  },
  content: {
    flex: 1,
    paddingTop: 24,
    paddingBottom: 24,
  },
});

export default CollapsibleSidebar;

